import { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  Drawer,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleTwoTone,
  ClockCircleTwoTone,
  CrownOutlined,
  EyeOutlined,
  FireOutlined,
  StarOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches } from '../hooks/useBatches';
import { useImChannel } from '../hooks/useImChannel';
import { taskStatusColor, taskStatusLabel } from '../utils/taskStatusDisplay';
import { Q07_HP_DEVELOP_PATHS } from '@api-common/hp-q07-develop-paths';

const { Text } = Typography;

const DEVELOP_PATH_NAME: Record<string, string> = Object.fromEntries(
  Q07_HP_DEVELOP_PATHS.map((p) => [p.code, p.name]),
);

interface CompetencyDimension {
  code: string;
  name: string;
  weight: number;
}

interface CompetencyModelLite {
  id: number;
  position: string;
  dimensions: CompetencyDimension[];
  applicableEmpIds: number[];
}

/** 与后端 CompetencyService.resolveModel 同优先级（前端镜像） */
function resolveCompetencyModel(
  models: CompetencyModelLite[],
  position: string | null | undefined,
  evaluateeId: number,
): CompetencyModelLite | null {
  if (!models || models.length === 0) return null;
  const empPos = (position || '').trim();
  const explicit = models.find((m) => m.applicableEmpIds?.includes(evaluateeId));
  if (explicit) return explicit;
  if (!empPos) return null;
  const splitPos = (s: string) =>
    s.split(/[&/、，,；;]/).map((x) => x.trim()).filter(Boolean);
  const exact = models.find((m) => (m.position || '').trim() === empPos);
  if (exact) return exact;
  const merged = models.find((m) => splitPos(m.position || '').includes(empPos));
  if (merged) return merged;
  const empParts = splitPos(empPos);
  if (empParts.length > 1) {
    const reverse = models.find((m) => empParts.includes((m.position || '').trim()));
    if (reverse) return reverse;
  }
  return null;
}

interface CompetencyResultRow {
  evaluateeId: number;
  evaluatorId: number | null;
  evaluator: { id: number; name: string; empNo?: string } | null;
  evaluatee: {
    id: number;
    name: string;
    empNo?: string;
    deptPath?: string | null;
    position?: string | null;
  };
  weightedScore: number | null;
  redLineFlag: boolean;
  status: string;
  /** 是否可评价（= 是否有直属领导）。无直属领导 → false，前端展示「不可评 / 无直属领导」。 */
  evaluable: boolean;
  submitAt: string | null;
  assessmentId: number | null;
  dimScores: Array<{ code: string; weight: number; score: number }> | null;
  redLines: Array<{ code: string; hit: boolean; note?: string; occurredAt?: string }> | null;
}

interface HpResultRow {
  id: number;
  evaluatorId: number;
  evaluateeId: number;
  evaluator?: { id: number; name: string; empNo?: string | null } | null;
  evaluatee?: {
    id: number;
    name: string;
    empNo?: string | null;
    deptPath?: string | null;
    position?: string | null;
  } | null;
  status: string;
  submitAt: string | null;
  isHighPotential: boolean;
  developPaths: string[] | null;
  developOther: string | null;
  starEvidences:
    | {
        scene?: string;
        task?: string;
        action?: string;
        result?: string;
        time?: string;
      }[]
    | null;
  dimScores: Record<string, number> | null;
  resultLabel: string | null;
  recommendedLevel: string | null;
  starCount: number;
}

interface UnifiedRow {
  evaluateeId: number;
  evaluatee: {
    id: number;
    name: string;
    empNo?: string | null;
    deptPath?: string | null;
    position?: string | null;
  };
  competency: CompetencyResultRow | null;
  hp: HpResultRow | null;
}

/** 高潜资格的判定（与 high-potential.classifier 跑批口径一致：avg≥4 且每维≥3 且 STAR≥2） */
function judgeHpQualified(
  hp: HpResultRow | null,
  thresholds: { avg_min?: number; per_dim_min?: number; star_min?: number },
):
  | { ok: true; label: string }
  | { ok: false; reason: string }
  | { ok: 'NA'; reason: string } {
  if (!hp || hp.status !== 'SUBMITTED') return { ok: 'NA', reason: '高潜未提交' };
  if (!hp.isHighPotential) return { ok: 'NA', reason: '领导未识别为高潜' };
  const dims = hp.dimScores ?? {};
  const A = Number(dims.A);
  const B = Number(dims.B);
  const C = Number(dims.C);
  const D = Number(dims.D);
  if (![A, B, C, D].every((n) => Number.isFinite(n) && n > 0)) {
    return { ok: false, reason: '四维分数不全' };
  }
  const avg = (A + B + C + D) / 4;
  const minDim = Math.min(A, B, C, D);
  const avgMin = Number(thresholds.avg_min ?? 4);
  const perDimMin = Number(thresholds.per_dim_min ?? 3);
  const starMin = Number(thresholds.star_min ?? 2);
  if (avg < avgMin) return { ok: false, reason: `平均 ${avg.toFixed(2)} < ${avgMin}` };
  if (minDim < perDimMin) return { ok: false, reason: `最低维 ${minDim} < ${perDimMin}` };
  if (hp.starCount < starMin) return { ok: false, reason: `STAR ${hp.starCount} < ${starMin}` };
  return { ok: true, label: hp.resultLabel ?? '一般高潜' };
}

const REDLINE_CN: Record<string, string> = {
  V1: '红线一',
  V2: '红线二',
  V3: '红线三',
};
function redLineCn(code: string) {
  return REDLINE_CN[code] || code;
}

export function CompetencyHpUnifiedResultsPage() {
  const { label: imLabel } = useImChannel();
  const { batches, batchId, setBatchId } = useBatches();
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'both-submitted' | 'comp-only' | 'hp-only' | 'pending' | 'redline' | 'qualified-hp'
  >('all');
  const [deptFilter, setDeptFilter] = useState<string | undefined>(undefined);
  const [positionFilter, setPositionFilter] = useState<string | undefined>(undefined);
  const [activeRow, setActiveRow] = useState<UnifiedRow | null>(null);

  const { data: compData = [], isFetching: compFetching } = useQuery<CompetencyResultRow[]>({
    queryKey: ['unified-comp', batchId],
    queryFn: () =>
      api.get('/admin/competency/results', { params: { batchId } }) as Promise<
        CompetencyResultRow[]
      >,
    enabled: !!batchId,
  });

  const { data: hpData = [], isFetching: hpFetching } = useQuery<HpResultRow[]>({
    queryKey: ['unified-hp', batchId],
    queryFn: () =>
      api.get('/admin/high-potential/results', { params: { batchId } }) as Promise<HpResultRow[]>,
    enabled: !!batchId,
  });

  const { data: competencyModels = [] } = useQuery<CompetencyModelLite[]>({
    queryKey: ['unified-competency-models'],
    queryFn: () =>
      api.get('/admin/competency/models') as Promise<CompetencyModelLite[]>,
  });

  const { data: hpConfig } = useQuery<{
    config: { thresholds?: { avg_min?: number; per_dim_min?: number; star_min?: number } } | null;
  }>({
    queryKey: ['unified-hp-config', batchId],
    queryFn: () =>
      api.get('/admin/high-potential/config', { params: { batchId } }) as Promise<{
        config: {
          thresholds?: { avg_min?: number; per_dim_min?: number; star_min?: number };
        } | null;
      }>,
    enabled: !!batchId,
  });
  const thresholds = hpConfig?.config?.thresholds ?? {
    avg_min: 4,
    per_dim_min: 3,
    star_min: 2,
  };

  /** 同一被评人 = 同一行；以胜任力名册为基准（若仅有高潜数据，行也补上）。 */
  const rows: UnifiedRow[] = useMemo(() => {
    const map = new Map<number, UnifiedRow>();
    for (const c of compData) {
      map.set(c.evaluateeId, {
        evaluateeId: c.evaluateeId,
        evaluatee: c.evaluatee,
        competency: c,
        hp: null,
      });
    }
    for (const h of hpData) {
      const existing = map.get(h.evaluateeId);
      if (existing) {
        existing.hp = h;
      } else {
        map.set(h.evaluateeId, {
          evaluateeId: h.evaluateeId,
          evaluatee: {
            id: h.evaluateeId,
            name: h.evaluatee?.name ?? `员工#${h.evaluateeId}`,
            empNo: h.evaluatee?.empNo ?? null,
            deptPath: h.evaluatee?.deptPath ?? null,
            position: h.evaluatee?.position ?? null,
          },
          competency: null,
          hp: h,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.evaluatee.name.localeCompare(b.evaluatee.name, 'zh-Hans-CN'),
    );
  }, [compData, hpData]);

  /** 部门 / 职位下拉选项：从当前批次数据里去重生成 */
  const deptOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const d = (r.evaluatee.deptPath ?? '').trim();
      if (d) set.add(d);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'))
      .map((d) => ({ value: d, label: d.split('/').slice(-2).join(' / ') }));
  }, [rows]);

  const positionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const p = (r.evaluatee.position ?? '').trim();
      if (p) set.add(p);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'))
      .map((p) => ({ value: p, label: p }));
  }, [rows]);

  const stats = useMemo(() => {
    const rosterActive = rows.length;
    const noLeaderCount = rows.filter((r) => r.competency?.evaluable === false).length;
    // 完成率/各项统计只针对「可评价」（有直属领导）人员，避免无直属领导者拉低分母。
    const evaluableRows = rows.filter((r) => r.competency?.evaluable !== false);
    const total = evaluableRows.length;
    const compSubmitted = evaluableRows.filter((r) => r.competency?.status === 'SUBMITTED').length;
    const hpSubmitted = evaluableRows.filter((r) => r.hp?.status === 'SUBMITTED').length;
    const bothSubmitted = evaluableRows.filter(
      (r) => r.competency?.status === 'SUBMITTED' && r.hp?.status === 'SUBMITTED',
    ).length;
    const redLineHit = evaluableRows.filter((r) => r.competency?.redLineFlag).length;
    const hpYes = evaluableRows.filter((r) => r.hp?.isHighPotential).length;
    const qualifiedHp = evaluableRows.filter(
      (r) => judgeHpQualified(r.hp, thresholds).ok === true,
    ).length;
    const percentBoth = total ? Math.round((bothSubmitted / total) * 100) : 0;
    return {
      rosterActive,
      noLeaderCount,
      total,
      compSubmitted,
      hpSubmitted,
      bothSubmitted,
      redLineHit,
      hpYes,
      qualifiedHp,
      percentBoth,
    };
  }, [rows, thresholds]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return rows.filter((r) => {
      if (deptFilter && (r.evaluatee.deptPath ?? '').trim() !== deptFilter) return false;
      if (positionFilter && (r.evaluatee.position ?? '').trim() !== positionFilter) return false;
      const compDone = r.competency?.status === 'SUBMITTED';
      const hpDone = r.hp?.status === 'SUBMITTED';
      switch (statusFilter) {
        case 'both-submitted':
          if (!(compDone && hpDone)) return false;
          break;
        case 'comp-only':
          if (!(compDone && !hpDone)) return false;
          break;
        case 'hp-only':
          if (!(hpDone && !compDone)) return false;
          break;
        case 'pending':
          if (compDone && hpDone) return false;
          break;
        case 'redline':
          if (!r.competency?.redLineFlag) return false;
          break;
        case 'qualified-hp':
          if (judgeHpQualified(r.hp, thresholds).ok !== true) return false;
          break;
      }
      if (!kw) return true;
      const hay = [
        r.evaluatee.name,
        r.evaluatee.position ?? '',
        r.evaluatee.deptPath ?? '',
        r.competency?.evaluator?.name ?? '',
        r.hp?.evaluator?.name ?? '',
      ].join(' ');
      return hay.includes(kw);
    });
  }, [rows, keyword, statusFilter, deptFilter, positionFilter, thresholds]);

  const isFetching = compFetching || hpFetching;

  /** 无直属领导（不可评）。后端 PR-4 方案 B：这些人也进名册，但标记不可评。 */
  const isNoLeader = (r: UnifiedRow) => r.competency?.evaluable === false;

  return (
    <div>
      <div className="tg-page-title" style={{ marginBottom: 4 }}>
        胜任力 + 高潜识别 · 评价汇总
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        以被评人为单位，把领导提交的「岗位胜任力评价」与「高潜识别」结果合并展示，不再分页查看。
      </Typography.Paragraph>

      {/* 顶部筛选 */}
      <Card style={{ marginBottom: 12 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12}>
          <span>批次：</span>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            value={batchId}
            options={batches.map((b: any) => ({ label: b.name, value: b.id }))}
            onChange={setBatchId}
          />
          <span style={{ marginLeft: 8 }}>状态：</span>
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            style={{ width: 200 }}
            options={[
              { value: 'all', label: '全部' },
              { value: 'both-submitted', label: '两项均已提交' },
              { value: 'comp-only', label: '仅胜任力已提交' },
              { value: 'hp-only', label: '仅高潜已提交' },
              { value: 'pending', label: '存在待提交' },
              { value: 'redline', label: '触发红线' },
              { value: 'qualified-hp', label: '达标高潜（avg≥4 等）' },
            ]}
          />
          <span style={{ marginLeft: 8 }}>部门：</span>
          <Select
            placeholder="全部部门"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            value={deptFilter}
            options={deptOptions}
            onChange={(v) => setDeptFilter(v)}
          />
          <span style={{ marginLeft: 8 }}>职位：</span>
          <Select
            placeholder="全部职位"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 180 }}
            value={positionFilter}
            options={positionOptions}
            onChange={(v) => setPositionFilter(v)}
          />
          <Input.Search
            placeholder="搜索被评价人 / 直属领导 / 部门 / 职位"
            allowClear
            style={{ width: 280 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </Space>

        <Row gutter={16} style={{ marginTop: 12 }}>
          <Col span={4}>
            <Statistic
              title="应评价"
              value={stats.total}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              suffix="人"
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="两项均提交"
              value={stats.bothSubmitted}
              suffix={stats.total ? `/ ${stats.total}` : ''}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleTwoTone twoToneColor="#52c41a" />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="胜任力已提交"
              value={stats.compSubmitted}
              valueStyle={{ color: '#1677ff' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="高潜已提交"
              value={stats.hpSubmitted}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<CrownOutlined style={{ color: '#fa8c16' }} />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="触发红线"
              value={stats.redLineHit}
              suffix="人"
              valueStyle={{ color: stats.redLineHit ? '#ff4d4f' : '#999' }}
              prefix={<WarningOutlined />}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="达标高潜"
              value={stats.qualifiedHp}
              suffix="人"
              valueStyle={{ color: stats.qualifiedHp ? '#fa8c16' : '#999' }}
              prefix={<FireOutlined />}
            />
          </Col>
        </Row>
        <Progress
          percent={stats.percentBoth}
          size="small"
          showInfo={false}
          style={{ marginTop: 8 }}
          strokeColor={{ '0%': '#722ed1', '100%': '#52c41a' }}
        />
        <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
          上方进度条 = 同一被评人「胜任力 + 高潜」均已提交的占比
        </div>
      </Card>

      {batchId && stats.rosterActive > 0 ? (
        <Alert
          type={stats.noLeaderCount > 0 ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 12 }}
          message={`在职 ${stats.rosterActive} 人 · 可评价 ${stats.total} 人 · 无直属领导 ${stats.noLeaderCount} 人`}
          description={
            stats.noLeaderCount > 0
              ? `「无直属领导」人员已纳入名册但暂不可评价（下表标「不可评」）。请 HR 在花名册补充直属上级或完善${imLabel}汇报链后刷新。`
              : '全部在职人员均有直属领导，可正常评价。'
          }
        />
      ) : null}

      {!batchId ? (
        <Alert type="info" showIcon message="请先选择批次" />
      ) : (
        <Card>
          <Table<UnifiedRow>
            rowKey={(r) => r.evaluateeId}
            dataSource={filtered}
            loading={isFetching}
            pagination={{ pageSize: 15, showTotal: (n) => `共 ${n} 人` }}
            columns={[
              {
                title: '被评人',
                key: 'evaluatee',
                width: 140,
                fixed: 'left' as const,
                render: (_v, r) => <Text strong>{r.evaluatee.name}</Text>,
              },
              {
                title: '部门 / 职位',
                key: 'dept',
                ellipsis: true,
                width: 220,
                render: (_v, r) => (
                  <Space direction="vertical" size={0}>
                    <Text style={{ fontSize: 13 }}>
                      {(r.evaluatee.deptPath ?? '-').split('/').slice(-2).join(' / ')}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {r.evaluatee.position ?? '-'}
                    </Text>
                  </Space>
                ),
              },
              {
                title: '胜任力',
                children: [
                  {
                    title: '加权分',
                    key: 'compScore',
                    width: 100,
                    sorter: (a: UnifiedRow, b: UnifiedRow) =>
                      (a.competency?.weightedScore ?? -Infinity) -
                      (b.competency?.weightedScore ?? -Infinity),
                    sortDirections: ['descend' as const, 'ascend' as const],
                    render: (_v, r) =>
                      isNoLeader(r) ? (
                        <Text type="secondary">不可评</Text>
                      ) : r.competency?.weightedScore != null ? (
                        <Text
                          strong
                          style={{
                            color:
                              r.competency.weightedScore >= 80
                                ? '#52c41a'
                                : r.competency.weightedScore >= 60
                                  ? '#1677ff'
                                  : '#fa8c16',
                          }}
                        >
                          {r.competency.weightedScore.toFixed(2)}
                        </Text>
                      ) : (
                        <Text type="secondary">-</Text>
                      ),
                  },
                  {
                    title: '红线',
                    key: 'redline',
                    width: 80,
                    render: (_v, r) =>
                      isNoLeader(r) ? (
                        <Text type="secondary">-</Text>
                      ) : r.competency?.redLineFlag ? (
                        <Tag color="red">已触发</Tag>
                      ) : r.competency ? (
                        <Tag color="default">未触发</Tag>
                      ) : (
                        <Text type="secondary">-</Text>
                      ),
                  },
                  {
                    title: '状态',
                    key: 'compStatus',
                    width: 110,
                    render: (_v, r) =>
                      isNoLeader(r) ? (
                        <Tag color="orange">无直属领导</Tag>
                      ) : r.competency ? (
                        <Tag color={taskStatusColor(r.competency.status)}>
                          {taskStatusLabel(r.competency.status)}
                        </Tag>
                      ) : (
                        <Tag color="default">未派</Tag>
                      ),
                  },
                ],
              },
              {
                title: '高潜识别',
                children: [
                  {
                    title: '识别',
                    key: 'hpYes',
                    width: 100,
                    render: (_v, r) => {
                      if (isNoLeader(r)) return <Text type="secondary">不可评</Text>;
                      if (!r.hp || r.hp.status !== 'SUBMITTED')
                        return <Text type="secondary">-</Text>;
                      return r.hp.isHighPotential ? (
                        <Tag color="gold" icon={<CrownOutlined />}>
                          是·高潜
                        </Tag>
                      ) : (
                        <Tag color="blue">否</Tag>
                      );
                    },
                  },
                  {
                    title: (
                      <Tooltip title="A 成长性 / B 雄心抱负 / C 学习意愿 / D 价值观契合 的算术平均">
                        四维平均
                      </Tooltip>
                    ),
                    key: 'hpAvg',
                    width: 100,
                    render: (_v, r) => {
                      const d = r.hp?.dimScores ?? null;
                      if (!d) return <Text type="secondary">-</Text>;
                      const A = Number(d.A);
                      const B = Number(d.B);
                      const C = Number(d.C);
                      const D = Number(d.D);
                      if (![A, B, C, D].every((n) => Number.isFinite(n) && n > 0)) {
                        return <Text type="secondary">-</Text>;
                      }
                      const avg = (A + B + C + D) / 4;
                      const ok = avg >= Number(thresholds.avg_min ?? 4);
                      return (
                        <Tooltip
                          title={`A=${A} / B=${B} / C=${C} / D=${D}（门槛 ≥ ${thresholds.avg_min ?? 4}）`}
                        >
                          <Text
                            strong
                            style={{ color: ok ? '#52c41a' : '#fa8c16' }}
                          >
                            {avg.toFixed(2)}
                          </Text>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: 'STAR',
                    key: 'star',
                    width: 80,
                    render: (_v, r) =>
                      r.hp ? (
                        <Tag
                          color={
                            r.hp.starCount >= Number(thresholds.star_min ?? 2)
                              ? 'gold'
                              : 'default'
                          }
                          icon={<StarOutlined />}
                        >
                          {r.hp.starCount} 条
                        </Tag>
                      ) : (
                        <Text type="secondary">-</Text>
                      ),
                  },
                  {
                    title: '资格判定',
                    key: 'qualified',
                    width: 130,
                    render: (_v, r) => {
                      const j = judgeHpQualified(r.hp, thresholds);
                      if (j.ok === true)
                        return (
                          <Tag color="gold" icon={<FireOutlined />}>
                            {j.label}
                          </Tag>
                        );
                      if (j.ok === 'NA')
                        return (
                          <Tooltip title={j.reason}>
                            <Tag color="default">—</Tag>
                          </Tooltip>
                        );
                      return (
                        <Tooltip title={j.reason}>
                          <Tag color="orange">不达标</Tag>
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: '状态',
                    key: 'hpStatus',
                    width: 100,
                    render: (_v, r) =>
                      isNoLeader(r) ? (
                        <Tag color="orange">无直属领导</Tag>
                      ) : r.hp ? (
                        <Tag color={taskStatusColor(r.hp.status)}>
                          {taskStatusLabel(r.hp.status)}
                        </Tag>
                      ) : (
                        <Tag color="default">未派</Tag>
                      ),
                  },
                ],
              },
              {
                title: '操作',
                key: 'action',
                fixed: 'right' as const,
                width: 90,
                render: (_v, r) => (
                  <a onClick={() => setActiveRow(r)}>
                    <EyeOutlined /> 详情
                  </a>
                ),
              },
            ]}
            scroll={{ x: 1280 }}
          />
        </Card>
      )}

      <Drawer
        open={!!activeRow}
        onClose={() => setActiveRow(null)}
        width={760}
        title={
          activeRow
            ? `${activeRow.evaluatee.name}（${activeRow.evaluatee.position ?? '-'}）· 评价详情`
            : ''
        }
      >
        {activeRow ? (
          <UnifiedDetail
            row={activeRow}
            thresholds={thresholds}
            competencyModels={competencyModels}
          />
        ) : null}
      </Drawer>
    </div>
  );
}

function UnifiedDetail({
  row,
  thresholds,
  competencyModels,
}: {
  row: UnifiedRow;
  thresholds: { avg_min?: number; per_dim_min?: number; star_min?: number };
  competencyModels: CompetencyModelLite[];
}) {
  const { label: imLabel } = useImChannel();
  const c = row.competency;
  const hp = row.hp;
  const judge = judgeHpQualified(hp, thresholds);
  const competencyModel = useMemo(
    () => resolveCompetencyModel(competencyModels, row.evaluatee.position, row.evaluatee.id),
    [competencyModels, row.evaluatee.position, row.evaluatee.id],
  );
  const dimNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of competencyModel?.dimensions ?? []) {
      m.set(d.code, d.name);
    }
    return m;
  }, [competencyModel]);
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        size="small"
        title={
          <Space>
            <Tag color="purple">岗位胜任力</Tag>
            <Text strong>评价结果</Text>
          </Space>
        }
      >
        {c && c.evaluable === false ? (
          <Text type="secondary">
            该员工无直属领导，暂不可评价；请在花名册补充直属上级或完善{imLabel}汇报链后刷新。
          </Text>
        ) : c ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">直属领导：</Text>
              <Text>{c.evaluator?.name ?? '-'}</Text>
              <Text type="secondary" style={{ marginLeft: 16 }}>
                状态：
              </Text>
              <Tag color={taskStatusColor(c.status)}>{taskStatusLabel(c.status)}</Tag>
              <Text type="secondary" style={{ marginLeft: 16 }}>
                提交时间：
              </Text>
              <Text>{c.submitAt ? new Date(c.submitAt).toLocaleString() : '-'}</Text>
            </div>
            <div>
              <Text type="secondary">加权分：</Text>
              <Text strong style={{ fontSize: 16, color: '#1677ff' }}>
                {c.weightedScore != null ? c.weightedScore.toFixed(2) : '-'}
              </Text>
            </div>
            {c.dimScores?.length ? (
              <div>
                <Text type="secondary">维度得分：</Text>
                <Space wrap size={8} style={{ marginTop: 4 }}>
                  {c.dimScores.map((d) => (
                    <Tag key={d.code} color="blue">
                      {dimNameByCode.get(d.code) || d.code}（权重 {d.weight}%）：{d.score} 分
                    </Tag>
                  ))}
                </Space>
              </div>
            ) : null}
            {c.redLines?.some((r) => r.hit) ? (
              <div>
                <Text type="danger" strong>
                  触发红线：
                </Text>
                <Space direction="vertical" size={4} style={{ marginTop: 4, width: '100%' }}>
                  {c.redLines
                    .filter((r) => r.hit)
                    .map((r, i) => (
                      <Card
                        key={i}
                        size="small"
                        style={{ borderLeft: '4px solid #ff4d4f' }}
                        styles={{ body: { padding: 8 } }}
                      >
                        <Space direction="vertical" size={2}>
                          <Tag color="red">{redLineCn(r.code)}</Tag>
                          {r.note ? <Text>{r.note}</Text> : null}
                          {r.occurredAt ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              发生时间：{r.occurredAt}
                            </Text>
                          ) : null}
                        </Space>
                      </Card>
                    ))}
                </Space>
              </div>
            ) : (
              <Tag color="default">未触发任何红线</Tag>
            )}
          </Space>
        ) : (
          <Text type="secondary">该被评人尚无胜任力评价记录</Text>
        )}
      </Card>

      <Card
        size="small"
        title={
          <Space>
            <Tag color="gold" icon={<CrownOutlined />}>
              高潜识别
            </Tag>
            <Text strong>评价结果</Text>
          </Space>
        }
      >
        {hp ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">识别人：</Text>
              <Text>{hp.evaluator?.name ?? '-'}</Text>
              <Text type="secondary" style={{ marginLeft: 16 }}>
                状态：
              </Text>
              <Tag color={taskStatusColor(hp.status)}>{taskStatusLabel(hp.status)}</Tag>
              <Text type="secondary" style={{ marginLeft: 16 }}>
                提交时间：
              </Text>
              <Text>{hp.submitAt ? new Date(hp.submitAt).toLocaleString() : '-'}</Text>
            </div>
            <div>
              <Text type="secondary">领导识别：</Text>
              {hp.isHighPotential ? (
                <Tag color="gold" icon={<CrownOutlined />}>
                  是·高潜
                </Tag>
              ) : (
                <Tag color="blue">否·非高潜</Tag>
              )}
              <Text type="secondary" style={{ marginLeft: 16 }}>
                资格判定：
              </Text>
              {judge.ok === true ? (
                <Tag color="gold">{judge.label}</Tag>
              ) : judge.ok === 'NA' ? (
                <Tag color="default">{judge.reason}</Tag>
              ) : (
                <Tag color="orange">不达标 · {judge.reason}</Tag>
              )}
            </div>
            {hp.dimScores ? (
              <div>
                <Text type="secondary">四维得分：</Text>
                <Space wrap size={8} style={{ marginTop: 4 }}>
                  {(['A', 'B', 'C', 'D'] as const).map((k) => (
                    <Tag key={k} color="gold">
                      {k}：{hp.dimScores?.[k] ?? '-'} 分
                    </Tag>
                  ))}
                </Space>
              </div>
            ) : null}
            {hp.starEvidences?.length ? (
              <div>
                <Text type="secondary">STAR 案例（{hp.starEvidences.length} 条）：</Text>
                <Space direction="vertical" size={6} style={{ marginTop: 4, width: '100%' }}>
                  {hp.starEvidences.map((s, i) => (
                    <Card
                      key={i}
                      size="small"
                      style={{ borderLeft: '4px solid #fa8c16' }}
                      styles={{ body: { padding: 8 } }}
                    >
                      <div style={{ fontSize: 12 }}>
                        <Text type="secondary">S：</Text>
                        <Text>{s.scene ?? '-'}</Text>
                      </div>
                      <div style={{ fontSize: 12 }}>
                        <Text type="secondary">T：</Text>
                        <Text>{s.task ?? '-'}</Text>
                      </div>
                      <div style={{ fontSize: 12 }}>
                        <Text type="secondary">A：</Text>
                        <Text>{s.action ?? '-'}</Text>
                      </div>
                      <div style={{ fontSize: 12 }}>
                        <Text type="secondary">R：</Text>
                        <Text>{s.result ?? '-'}</Text>
                      </div>
                      {s.time ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          时间：{String(s.time).slice(0, 10)}
                        </Text>
                      ) : null}
                    </Card>
                  ))}
                </Space>
              </div>
            ) : null}
            {hp.developPaths?.length ? (
              <div>
                <Text type="secondary">培养方向：</Text>
                <Space direction="vertical" size={4} style={{ marginTop: 4, width: '100%' }}>
                  {hp.developPaths.map((p) => (
                    <Tag key={p} color="purple" style={{ whiteSpace: 'normal', lineHeight: 1.6 }}>
                      {DEVELOP_PATH_NAME[p] || p}
                    </Tag>
                  ))}
                  {hp.developOther ? (
                    <Tag color="purple" style={{ whiteSpace: 'normal', lineHeight: 1.6 }}>
                      其他：{hp.developOther}
                    </Tag>
                  ) : null}
                </Space>
              </div>
            ) : null}
          </Space>
        ) : (
          <Text type="secondary">该被评人尚无高潜识别记录</Text>
        )}
      </Card>
    </Space>
  );
}
